import type {
  AssignDevicesBody,
  AssignDevicesResponse,
  ListMembersResponse,
  MemberListItem,
} from '@m8-app/contracts/rest/members.js';
import type { AdminCreateMemberBody } from '@m8-app/contracts/rest/admin.js';
import type {
  ListFloorsResponse,
  ReplaceFloorsBody,
  ReplaceFloorsResponse,
} from '@m8-app/contracts/rest/floors.js';
import type {
  AdminFaqListResponse,
  FaqCreateBody,
  FaqEntry,
  FaqUpdateBody,
} from '@m8-app/contracts/rest/faq.js';
import type {
  AdminDeviceDetailResponse,
  AdminDeviceListResponse,
  AdminDeviceLogsResponse,
  AdminFirmwareListResponse,
  AdminRemoteActionBody,
  AdminRemoteActionResponse,
  AdminUserBlockBody,
  AdminUserListResponse,
  DashboardSummaryResponse,
  DeviceLifecycleHistoryResponse,
  FirmwareUploadBody,
  FirmwareUploadResponse,
  AdminProduct,
  NextSerialResponse,
  ProductionDeviceCreateBody,
  ProductionDeviceCreateResponse,
  ReportHistoryResponse,
  ReportRequestBody,
  ReportSubmitResponse,
  RolloutStartBody,
  RolloutStartResponse,
  ServiceRequestCreateBody,
  ServiceRequestDetailResponse,
  ServiceRequestListResponse,
  ServiceRequestUpdateBody,
  WarrantyClaimCreateBody,
  WarrantyClaimCreateResponse,
  WarrantyClaimListResponse,
  WarrantyClaimUpdateBody,
  AdminDeviceWarrantyListResponse,
  SystemHealthResponse,
} from '@m8-app/contracts/rest/admin.js';
import type {
  AppRoleAssignBody,
  AppRoleAssignment,
  AppRoleGrantBody,
  AppRoleListResponse,
} from '@m8-app/contracts/rest/admin.js';
import type { OtpRequestResponse, OtpVerifyResponse } from '@m8-app/contracts/rest/auth.js';
import type { TestReportListResponse } from '@m8-app/contracts/rest/test-reports.js';
import type {
  WarrantyConfigListResponse,
  WarrantyConfigUpdateBody,
} from '@m8-app/contracts/rest/warranty.js';
import { apiClient } from './client.js';

// ── Auth ───────────────────────────────────────────────────────────────────

export async function requestOtp(mobileNumber: string): Promise<OtpRequestResponse> {
  const { data } = await apiClient.post<OtpRequestResponse>('/auth/otp/request', { mobileNumber });
  return data;
}

export async function verifyOtp(mobileNumber: string, otp: string): Promise<OtpVerifyResponse> {
  const { data } = await apiClient.post<OtpVerifyResponse>('/auth/otp/verify', { mobileNumber, otp });
  return data;
}

export async function logout(refreshToken: string): Promise<void> {
  await apiClient.post('/auth/logout', { refreshToken });
}

// ── System Health ──────────────────────────────────────────────────────────

export async function getSystemHealth(): Promise<SystemHealthResponse> {
  const { data } = await apiClient.get<SystemHealthResponse>('/admin/system-health');
  return data;
}

// ── Dashboard ──────────────────────────────────────────────────────────────

export async function getDashboardSummary(params?: {
  warrantyDays?: number; offlineDays?: number; serviceDays?: number;
}): Promise<DashboardSummaryResponse> {
  const { data } = await apiClient.get<DashboardSummaryResponse>('/admin/dashboard/summary', { params });
  return data;
}

// ── Users ──────────────────────────────────────────────────────────────────

export async function getUsers(params?: {
  cursor?: string; role?: string; search?: string;
}): Promise<AdminUserListResponse> {
  const { data } = await apiClient.get<AdminUserListResponse>('/admin/users', { params });
  return data;
}

export async function blockUser(id: string, body: AdminUserBlockBody): Promise<void> {
  await apiClient.post(`/admin/users/${id}/block`, body);
}

export async function deleteUser(id: string): Promise<void> {
  await apiClient.delete(`/admin/users/${id}`);
}

// ── App Roles (mobile-app role hierarchy) ──────────────────────────────────

export async function listAppRoles(): Promise<AppRoleListResponse> {
  const { data } = await apiClient.get<AppRoleListResponse>('/admin/app-roles');
  return data;
}

export async function assignAppRole(body: AppRoleAssignBody): Promise<AppRoleAssignment> {
  const { data } = await apiClient.post<AppRoleAssignment>('/admin/app-roles/assign', body);
  return data;
}

export async function revokeAppRole(userId: string): Promise<void> {
  await apiClient.delete(`/admin/app-roles/${userId}`);
}

export async function grantAppPermission(body: AppRoleGrantBody): Promise<void> {
  await apiClient.post('/admin/app-roles/grant', body);
}

export async function revokeAppPermission(body: AppRoleGrantBody): Promise<void> {
  await apiClient.delete('/admin/app-roles/grant', { data: body });
}

// ── Production test reports ─────────────────────────────────────────────────

export async function getTestReports(params?: {
  cursor?: string; testerId?: string; serial?: string;
}): Promise<TestReportListResponse> {
  const { data } = await apiClient.get<TestReportListResponse>('/admin/test-reports', { params });
  return data;
}

// ── Warranty ───────────────────────────────────────────────────────────────

export async function getDeviceWarranties(
  cursor?: string,
  status?: string,
  search?: string,
): Promise<AdminDeviceWarrantyListResponse> {
  const params: Record<string, string> = {};
  if (cursor) params.cursor = cursor;
  if (status && status !== 'all') params.status = status;
  if (search) params.search = search;
  const { data } = await apiClient.get<AdminDeviceWarrantyListResponse>('/admin/warranty/devices', {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

export async function getWarranty(cursor?: string, state?: string): Promise<WarrantyClaimListResponse> {
  const params: Record<string, string> = {};
  if (cursor) params.cursor = cursor;
  if (state && state !== 'all') params.state = state;
  const { data } = await apiClient.get<WarrantyClaimListResponse>('/admin/warranty', {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

export async function createWarranty(body: WarrantyClaimCreateBody): Promise<WarrantyClaimCreateResponse> {
  const { data } = await apiClient.post<WarrantyClaimCreateResponse>('/admin/warranty', body);
  return data;
}

export async function updateWarranty(id: string, body: WarrantyClaimUpdateBody): Promise<void> {
  await apiClient.post(`/admin/warranty/${id}`, body);
}

export async function setWarrantyRemark(id: string, remark: string): Promise<void> {
  await apiClient.patch(`/admin/warranty/${id}/remark`, { remark });
}

// ── Warranty configuration (per-product-type default duration) ─────────────

export async function getWarrantyConfig(): Promise<WarrantyConfigListResponse> {
  const { data } = await apiClient.get<WarrantyConfigListResponse>('/admin/warranty-config');
  return data;
}

export async function updateWarrantyConfig(
  body: WarrantyConfigUpdateBody,
): Promise<WarrantyConfigListResponse> {
  const { data } = await apiClient.patch<WarrantyConfigListResponse>('/admin/warranty-config', body);
  return data;
}

// ── Firmware ───────────────────────────────────────────────────────────────

export async function listFirmware(): Promise<AdminFirmwareListResponse> {
  const { data } = await apiClient.get<AdminFirmwareListResponse>('/admin/firmware');
  return data;
}

export async function uploadFirmware(body: FirmwareUploadBody): Promise<FirmwareUploadResponse> {
  const { data } = await apiClient.post<FirmwareUploadResponse>('/admin/firmware', body);
  return data;
}

export async function setFirmwareRemark(id: string, remark: string): Promise<void> {
  await apiClient.patch(`/admin/firmware/${id}/remark`, { remark });
}

export async function startRollout(firmwareVersionId: string, body: RolloutStartBody): Promise<RolloutStartResponse> {
  const { data } = await apiClient.post<RolloutStartResponse>(
    `/admin/firmware/${firmwareVersionId}/rollout`,
    body,
  );
  return data;
}

// ── Devices (admin-wide) ───────────────────────────────────────────────────

export async function getAdminDevices(params?: {
  cursor?: string; state?: string; presence?: string; search?: string;
  product?: string; lifecycle?: string;
}): Promise<AdminDeviceListResponse> {
  const { data } = await apiClient.get<AdminDeviceListResponse>('/admin/devices', { params });
  return data;
}

export async function getDeviceDetail(id: string): Promise<AdminDeviceDetailResponse> {
  const { data } = await apiClient.get<AdminDeviceDetailResponse>(`/admin/devices/${id}`);
  return data;
}

export async function getDeviceLogs(id: string, limit = 100): Promise<AdminDeviceLogsResponse> {
  const { data } = await apiClient.get<AdminDeviceLogsResponse>(`/admin/devices/${id}/logs`, {
    params: { limit },
  });
  return data;
}

export async function setDeviceRemark(id: string, remark: string): Promise<void> {
  await apiClient.patch(`/admin/devices/${id}/remark`, { remark });
}

/**
 * Force-unclaims a device regardless of owner — clears owner/name/location,
 * config, sensors and member access, but leaves device_identity (serial,
 * secret key) untouched so the hardware stays re-claimable afterward.
 */
export async function removeDevice(id: string): Promise<void> {
  await apiClient.delete(`/admin/devices/${id}`);
}

// ── Production ─────────────────────────────────────────────────────────────

/** Next `<PREFIX>MMYY-BB-Snnn` for the Add Device modal; `batch` previews a different batch. */
export async function getNextSerial(product: AdminProduct, batch?: number): Promise<NextSerialResponse> {
  const { data } = await apiClient.get<NextSerialResponse>('/admin/production/next-serial', {
    params: { product, ...(batch ? { batch } : {}) },
  });
  return data;
}

export async function createProductionDevice(
  body: ProductionDeviceCreateBody,
): Promise<ProductionDeviceCreateResponse> {
  const { data } = await apiClient.post<ProductionDeviceCreateResponse>(
    '/admin/production/devices',
    body,
  );
  return data;
}

export async function getDeviceLifecycleHistory(
  deviceId: string,
): Promise<DeviceLifecycleHistoryResponse> {
  const { data } = await apiClient.get<DeviceLifecycleHistoryResponse>(
    `/admin/devices/${deviceId}/lifecycle-events`,
  );
  return data;
}

// ── Service requests ───────────────────────────────────────────────────────

export async function getServiceRequests(params?: {
  cursor?: string; state?: string; deviceId?: string; assignedAdminId?: string;
}): Promise<ServiceRequestListResponse> {
  const { data } = await apiClient.get<ServiceRequestListResponse>('/admin/service-requests', { params });
  return data;
}

export async function getServiceRequest(id: string): Promise<ServiceRequestDetailResponse> {
  const { data } = await apiClient.get<ServiceRequestDetailResponse>(`/admin/service-requests/${id}`);
  return data;
}

export async function createServiceRequest(body: ServiceRequestCreateBody): Promise<{ id: string }> {
  const { data } = await apiClient.post<{ id: string }>('/admin/service-requests', body);
  return data;
}

export async function updateServiceRequest(id: string, body: ServiceRequestUpdateBody): Promise<void> {
  await apiClient.patch(`/admin/service-requests/${id}`, body);
}

// ── Reports ────────────────────────────────────────────────────────────────

export async function runReport(body: ReportRequestBody): Promise<ReportSubmitResponse> {
  const { data } = await apiClient.post<ReportSubmitResponse>('/admin/reports', body);
  return data;
}

export async function getReportHistory(cursor?: string): Promise<ReportHistoryResponse> {
  const { data } = await apiClient.get<ReportHistoryResponse>('/admin/reports/history', {
    params: cursor ? { cursor } : undefined,
  });
  return data;
}

export async function downloadReport(id: string): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/admin/reports/${id}/download`, {
    responseType: 'blob',
  });
  return data;
}

// ── Remote actions ─────────────────────────────────────────────────────────

export async function remoteAction(
  deviceId: string,
  body: AdminRemoteActionBody,
): Promise<AdminRemoteActionResponse> {
  const { data } = await apiClient.post<AdminRemoteActionResponse>(
    `/admin/devices/${deviceId}/remote-action`,
    body,
  );
  return data;
}

/** Staff recovery path: reassign a device's owner (Main User) to a mobile
 *  number. super_admin/admin only — audited server-side. */
export async function reassignDeviceOwner(
  deviceId: string,
  mobile: string,
): Promise<{ deviceId: string; ownerMobile: string }> {
  const { data } = await apiClient.post<{ deviceId: string; ownerMobile: string }>(
    `/admin/devices/${deviceId}/reassign-owner`,
    { mobile },
  );
  return data;
}

// ── Members (admin-scoped) ─────────────────────────────────────────────────

export async function getAdminMembers(): Promise<ListMembersResponse> {
  const { data } = await apiClient.get<ListMembersResponse>('/admin/members');
  return data;
}

export async function adminCreateMember(body: AdminCreateMemberBody): Promise<MemberListItem> {
  const { data } = await apiClient.post<MemberListItem>('/admin/members', body);
  return data;
}

export async function adminDeleteMember(id: string): Promise<void> {
  await apiClient.delete(`/admin/members/${id}`);
}

export async function adminAssignDevicesToMember(
  memberId: string,
  body: AssignDevicesBody,
): Promise<AssignDevicesResponse> {
  const { data } = await apiClient.post<AssignDevicesResponse>(`/admin/members/${memberId}/devices`, body);
  return data;
}

// ── Floors ─────────────────────────────────────────────────────────────────

export async function getDeviceFloors(deviceId: string): Promise<ListFloorsResponse> {
  const { data } = await apiClient.get<ListFloorsResponse>(`/devices/${deviceId}/floors`);
  return data;
}

export async function replaceDeviceFloors(
  deviceId: string,
  body: ReplaceFloorsBody,
): Promise<ReplaceFloorsResponse> {
  const { data } = await apiClient.put<ReplaceFloorsResponse>(`/devices/${deviceId}/floors`, body);
  return data;
}

// ── FAQ (HRD-8) ──────────────────────────────────────────────────────────

export async function listFaq(): Promise<AdminFaqListResponse> {
  const { data } = await apiClient.get<AdminFaqListResponse>('/admin/faq');
  return data;
}

export async function createFaq(body: FaqCreateBody): Promise<FaqEntry> {
  const { data } = await apiClient.post<FaqEntry>('/admin/faq', body);
  return data;
}

export async function updateFaq(id: string, body: FaqUpdateBody): Promise<FaqEntry> {
  const { data } = await apiClient.patch<FaqEntry>(`/admin/faq/${id}`, body);
  return data;
}

export async function deleteFaq(id: string): Promise<void> {
  await apiClient.delete(`/admin/faq/${id}`);
}


// ── Content Management (CMS-1/CMS-2) ────────────────────────────────────────

import type {
  AdminContentDocumentResponse,
  AdminDemoVideoListResponse,
  CreateDemoVideoBody,
  UpdateContentDocumentBody,
  UpdateDemoVideoBody,
  UploadThumbnailBody,
} from '@m8-app/contracts/rest/content.js';
import type { AdminDemoVideo } from '@m8-app/contracts/rest/content.js';

export async function getAdminContentDocument(slug: string): Promise<AdminContentDocumentResponse> {
  const { data } = await apiClient.get<AdminContentDocumentResponse>(`/admin/content/documents/${slug}`);
  return data;
}

export async function updateAdminContentDocument(
  slug: string,
  body: UpdateContentDocumentBody,
): Promise<AdminContentDocumentResponse> {
  const { data } = await apiClient.put<AdminContentDocumentResponse>(
    `/admin/content/documents/${slug}`,
    body,
  );
  return data;
}

export async function listAdminDemoVideos(): Promise<AdminDemoVideoListResponse> {
  const { data } = await apiClient.get<AdminDemoVideoListResponse>('/admin/demo-videos');
  return data;
}

export async function createAdminDemoVideo(body: CreateDemoVideoBody): Promise<AdminDemoVideo> {
  const { data } = await apiClient.post<AdminDemoVideo>('/admin/demo-videos', body);
  return data;
}

export async function updateAdminDemoVideo(id: string, body: UpdateDemoVideoBody): Promise<AdminDemoVideo> {
  const { data } = await apiClient.patch<AdminDemoVideo>(`/admin/demo-videos/${id}`, body);
  return data;
}

export async function deleteAdminDemoVideo(id: string): Promise<void> {
  await apiClient.delete(`/admin/demo-videos/${id}`);
}

export async function uploadVideoThumbnail(id: string, body: UploadThumbnailBody): Promise<void> {
  await apiClient.put(`/admin/demo-videos/${id}/thumbnail`, body);
}

// ── App Management — remote feature flags ───────────────────────────────────

import type {
  AdminAppFeature,
  AdminAppFeatureListResponse,
  AdminAppFeatureUpdateBody,
  AdminAppSettingsResponse,
  AdminAppSettingsUpdateBody,
} from '@m8-app/contracts/rest/app-config.js';

export async function getAppFeatures(): Promise<AdminAppFeatureListResponse> {
  const { data } = await apiClient.get<AdminAppFeatureListResponse>('/admin/app-features');
  return data;
}

export async function updateAppFeature(
  key: string,
  body: AdminAppFeatureUpdateBody,
): Promise<AdminAppFeature> {
  const { data } = await apiClient.patch<AdminAppFeature>(`/admin/app-features/${key}`, body);
  return data;
}

export async function deleteAppFeature(key: string): Promise<void> {
  await apiClient.delete(`/admin/app-features/${key}`);
}

// ── App Management — remote app settings (key/value, super_admin writes) ────

export async function getAppSettings(): Promise<AdminAppSettingsResponse> {
  const { data } = await apiClient.get<AdminAppSettingsResponse>('/admin/app-settings');
  return data;
}

export async function updateAppSettings(
  body: AdminAppSettingsUpdateBody,
): Promise<AdminAppSettingsResponse> {
  const { data } = await apiClient.patch<AdminAppSettingsResponse>('/admin/app-settings', body);
  return data;
}
